<template>
  <div class="swift-wrapper">
    <div class="swift-wrapper__tracker-row">
      <k-slide :appear="allConditionCategoriesLoaded && allConditionCategories.length > 0">
        <div class="tracker-row__containers">
          <div class="tracker-row__category-container" v-for="category in allConditionCategories" :key="category">
            <div v-bind:class="['tracker-row__btn', { 'btn--active': category.name === selectedTrackerBtn }]"
              v-on:click="onCategoryFilter(category.name)">
              <div v-if="category.cleared" class="category__cleared-container">
                <!-- Green, like a completed tile on Loan Activity. var(--accent) is still the
                     legacy teal outside dialogs, which is why this one glyph was off-palette. -->
                <span class="k-icon k-i-check-outline k-i-checkmark-outline k-icon-md tracker__cleared-icon"></span>
                <p> {{ category.name }}:&nbsp; <span>{{ category.numberCleared }}/{{ category.count }}</span></p>
              </div>
              <div v-if="!category.cleared" class="category__gauge-container">
                <k-circular-gauge :value="category.percentCleared" style="height: 40px; width: 80px;"
                  :colors="gaugeColors" :center-render="'guageCenterTemplate'">
                  <template v-slot:guageCenterTemplate="{ props }">
                    <h3>{{ props.value }}%</h3>
                  </template>
                </k-circular-gauge>
                <p>{{ category.name }}:&nbsp; <span>{{ category.numberCleared }}/{{ category.count }}</span></p>
              </div>
            </div>
          </div>
        </div>
      </k-slide>
    </div>

    <div class="swift-wrapper__content">
      <div class="swift-left">
        <div class="open-conditions-card">
        <!-- The view-header strip is gone: the global header bar already titles this page.
             Its five buttons split into the one people come here for (Add Conditions) plus an
             Actions menu, and the grid's own toolbar folded up into this head as well. -->
        <div class="conditions-head gs-panel__head">
          <h2 class="gs-panel__title">
            <span class="gs-badge-icon"><i class="fa-solid fa-list-check"></i></span>
            <span class="gs-panel__sub">Loan # {{ loanNumber }} &middot; {{ borrower }}</span>
          </h2>
          <div class="gs-panel__tools">
            <span class="gs-search">
              <i class="fa-solid fa-magnifying-glass gs-search__icon"></i>
              <k-input class="gs-search__input"
                       placeholder="Search conditions..."
                       :value="condSearchWord"
                       @input="onFilterConditions"></k-input>
            </span>
            <label class="gs-switch">
              <k-switch v-model="showAllConds" :on-label="''" :off-label="''" :size="'small'"></k-switch>
              <span class="gs-switch__label">Show Cleared</span>
            </label>
            <label class="gs-switch">
              <k-switch v-model="showInternalConds" :on-label="''" :off-label="''" :size="'small'"></k-switch>
              <span class="gs-switch__label">Show Internal</span>
            </label>
            <k-button class="gs-btn--ghost" @click="onBulkEditTracking" :disabled="bulkTrackingBtnDisabled">
              <span class="k-icon k-i-check k-i-checkmark"></span> Update Tracking
            </k-button>
            <k-button v-if="hasUnderwriterAccess" class="gs-btn--navy" @click="onAddConditions">
              <span class="k-icon k-i-plus"></span> Add Conditions
            </k-button>
            <div class="gs-actions" @click.stop>
              <k-button class="gs-btn--ghost"
                        :aria-expanded="condActionsOpen ? 'true' : 'false'"
                        @click="condActionsOpen = !condActionsOpen">
                Actions <i class="fa-solid fa-chevron-down gs-actions__chev" :class="{ 'is-open': condActionsOpen }"></i>
              </k-button>
              <div v-if="condActionsOpen" class="gs-actions__menu">
                <button v-if="hasUnderwriterAccess" type="button" class="gs-actions__item gs-actions__item--accent"
                        @click="runCondAction(onRunSwift)">
                  <i class="fa-duotone fa-wand-magic-sparkles"></i> Run swiftAI
                </button>
                <button v-if="hasUnderwriterAccess" type="button" class="gs-actions__item"
                        @click="runCondAction(generateApprovalLetter)">
                  <i class="fa-regular fa-file-lines"></i> Generate Approval Letter
                </button>
                <button type="button" class="gs-actions__item" @click="runCondAction(toggleLiabilitiesDialog)">
                  <i class="fa-regular fa-scale-balanced"></i> Liabilities
                </button>
                <button v-if="hasUnderwriterAccess" type="button" class="gs-actions__item"
                        @click="runCondAction(toggleDispositionWizard)">
                  <i class="fa-duotone fa-list-check"></i> Disposition Options
                </button>
              </div>
            </div>
          </div>
        </div>

          <k-tabstrip :selected="selectedConditionsTab" @select="onConditionsTabSelected" :tabs="conditionsTabs">
            <template v-slot:condgrid>
              <div class="grid__container">
                <Grid class="gs-grid" ref="condgridunderwriting" :data-items="conditions" :data-item-key="'id'" :columns="condColumns"
                  :sortable="sortable" :sort="condSort" :filterable="true" :filter="condFilter" :column-menu="true"
                  :selectable="true" :selected-field="selectedField" @selectionchange="onSelectionChange"
                  @headerselectionchange="onHeaderSelectionChange" @filterchange="condFilterChange"
                  @sortchange="conditionSortChangeHandler" @rowclick="onEditCondition">
                  <template v-slot:hasAttachments="{ props }">
                    <div class="k-table-td has-attachments__col"><span title="Condition Has Documents"
                        v-if="props.dataItem.hasAttachments" class="k-icon k-i-attachment-45 k-i-clip-45"></span></div>
                  </template>
                  <template v-slot:duplicateCondition="{ props }">
                    <div class="duplicate-condition-col">
                      <k-button title="Duplicate condition" v-on:click.stop="onDuplicateCondition(props.dataItem)"><span
                          class="k-icon k-i-copy k-i-files"></span></k-button>
                    </div>
                  </template>
                  <!-- Search, both switches and Update Tracking moved up into the panel
                       head; the "Conditions: N" count came out, same as the other grids. -->
                </Grid>
                <div class="conditions-update__loader-container" v-if="isLoadingUpdate">
                  <loader :size="'large'" :type="'infinite-spinner'"></loader>
                </div>
              </div>
            </template>
          </k-tabstrip>
        </div>
      </div>
    </div>

    <div class="conditions__loader-container" v-if="isLoading">
      <loader :size="'large'" :type="'infinite-spinner'"></loader>
    </div>

    <edit-condition-dialog :condition="selectedCondition" :show-dialog="showEditConditionDialog" :loan-id="loanid"
      :all-documents="allDocuments" v-on:shownotification="showNotification"
      v-on:closenotification="onCloseNotification" v-on:dialogclosed="toggleEditConditionDialog"
      v-on:updatecondition="updateCondition" v-on:updatetracking="onConditionTrackingUpdated"
      v-on:conditionupdated="onConditionUpdated" v-on:requesterror="onRequestError"></edit-condition-dialog>

    <add-condition-dialog :show-dialog="showAddConditionDialog" :loan-id="loanid" :current-conditions="allConditions"
      :addedConditionId="addedConditionId" :duplicatedCondition="duplicatedCondition"
      v-on:dialogclosed="toggleAddConditionDialog" v-on:conditionadded="onConditionAdded"
      v-on:requesterror="onRequestError"></add-condition-dialog>

    <add-conditions-dialog :show-dialog="showAddConditionsDialog" :loan-id="loanid" :current-conditions="allConditions"
      :addedConditionId="addedConditionId" v-on:dialogclosed="toggleAddConditionsDialog"
      v-on:conditionsadded="onConditionsAdded" v-on:requesterror="onRequestError"
      v-on:shownotification="showNotification('Adding conditions...', true)"
      v-on:closenotification="onCloseNotification('progressNotification')"></add-conditions-dialog>


    <blueprint-dialog :show-dialog="showBlueprintDialog" :loan-number="loanNumber" :loan-id="loanid"
      v-on:dialogclosed="toggleBlueprintDialog" v-on:requesterror="onRequestError"></blueprint-dialog>

    <bulk-edit-tracking-dialog :show-dialog="showBulkEditTrackingDialog" :selected-conditions="selectedConditions"
      v-on:bulktrackingupdated="bulkUpdateTracking"
      v-on:dialogclosed="toggleBulkEditTrackingDialog"></bulk-edit-tracking-dialog>

    <disposition-menu :show-dialog="showDispositionWizard" :loan-id="loanid" :attachments="attachments"
      :documents="allDocuments" v-on:dialogclosed="toggleDispositionWizard"
      v-on:completeconditionalapproval="completeConditionalApproval" v-on:completesuspend="completeSuspend"
      v-on:completedeny="completeDeny" v-on:completerestructure="completeRestructure"
      v-on:completecleartoclose="completeClearToClose" v-on:updatedocuments="updateDocuments"></disposition-menu>

    <liabilities-dialog :show-dialog="showLiabilitiesDialog" :loan-id="loanid"
      v-on:dialogclosed="toggleLiabilitiesDialog"></liabilities-dialog>

    <k-notification-group class="conditions__notification-container">
      <k-fade :appear="successNotification">
        <k-notification v-if="successNotification" :type="{ style: 'success', icon: true }" :closable="true"
          @close="onCloseNotification('successNotification')">
          <span>{{ notificationMsg }}</span>
        </k-notification>
      </k-fade>
      <k-fade :appear="progressNotification">
        <k-notification v-if="progressNotification" :type="{ style: 'warning', icon: true }" :closable="true"
          @close="onCloseNotification('progressNotification')">
          <span>{{ progressNotificationMsg }}</span>
        </k-notification>
      </k-fade>
    </k-notification-group>

    <transition name="dialogfade">
      <k-dialog v-if="showErrorDialog" :title="errorTitle" class="error__dialog" @close="toggleErrorDialog">
        <p class="error-dialog__msg">{{ errorMsg }}</p>
      </k-dialog>
    </transition>

    <enc-lock-warning-dialog :show-dialog="showEncLockWarningDialog"
      @dialogclosed="toggleEncLockWarningDialog"></enc-lock-warning-dialog>
  </div>
</template>

<script>
import auth from '../authConfig.js';
import apiService from '../api/api.service.js';
import formatHelpers from '../helpers/formatHelpers.js';

import cloneDeep from 'lodash.clonedeep';

import editConditionDialog from './editConditionDialog.vue';
import addConditionDialog from './addConditionDialog.vue';
import addConditionsDialog from './addConditionsDialog.vue';
import editDocumentDialog from './editDocumentDialog.vue';
import blueprintDialog from './blueprintDialog.vue';
import bulkEditTrackingDialog from './bulkEditTrackingDialog.vue';
import dispositionMenu from './dispositionMenu.vue';
import liabilitiesDialog from './liabilitiesDialog.vue';
import encLockWarningDialog from './encLockWarningDialog.vue';

import { Grid } from '@progress/kendo-vue-grid';
import { Button } from '@progress/kendo-vue-buttons';
import { Input, Switch } from "@progress/kendo-vue-inputs";
import { Loader } from "@progress/kendo-vue-indicators";
import { Dialog, DialogActionsBar, Window } from '@progress/kendo-vue-dialogs';
import { Notification, NotificationGroup } from "@progress/kendo-vue-notification";
import { Fade, Reveal, Slide } from "@progress/kendo-vue-animation";
import { TabStrip } from '@progress/kendo-vue-layout';
import { ArcGauge } from '@progress/kendo-vue-gauges';

import { orderBy, filterBy } from '@progress/kendo-data-query';

export default {
  props: ['loanid', 'allConditions', 'loanNumber', 'borrower'],
  emits: ['conditionsupdated'],
  name: 'App',
  components: {
    Grid: Grid,
    'k-button': Button,
    'k-input': Input,
    'loader': Loader,
    'k-dialog': Dialog,
    'k-dialog-actions': DialogActionsBar,
    'k-window': Window,
    'edit-condition-dialog': editConditionDialog,
    'k-notification': Notification,
    'k-notification-group': NotificationGroup,
    'k-fade': Fade,
    'k-slide': Slide,
    'add-condition-dialog': addConditionDialog,
    'add-conditions-dialog': addConditionsDialog,
    'k-tabstrip': TabStrip,
    'k-circular-gauge': ArcGauge,
    'k-switch': Switch,
    'edit-document-dialog': editDocumentDialog,
    'blueprint-dialog': blueprintDialog,
    'bulk-edit-tracking-dialog': bulkEditTrackingDialog,
    'disposition-menu': dispositionMenu,
    'liabilities-dialog': liabilitiesDialog,
    'enc-lock-warning-dialog': encLockWarningDialog
  },
  data() {
    return {
      // Actions menu in the panel head; closes on outside click or Escape.
      condActionsOpen: false,
      addedConditionId: '',
      addedConditionTitle: '',
      addedConditionTracking: null,
      allConditionCategories: [],
      allConditionCategoriesLoaded: false,
      isInitializing: true,
      allConditionCategoryModel: { name: '', cleared: false, count: 0 },
      allDocuments: [],
      allTabbedConditions: [],
      attachments: [],
      bulkTrackingBtnDisabled: true,
      conditions: [],
      condFilter: null,
      condSearchWord: '',
      condSort: [],
      conditionsTabs: [
        { title: 'Underwriting', content: 'condgrid', priorTo: ['Approval', 'Docs'] },
        { title: 'Closing', content: 'condgrid', priorTo: ['Closing'] },
        { title: 'Purchase', content: 'condgrid', priorTo: ['Purchase'] }
      ],
      conditionUpdated: false,
      customFields: [],
      dateColumns: ['createdDate', 'lastModifiedDate'],
      documentsUpdated: false,
      duplicatedCondition: null,
      duplicateMode: false,
      editedConditionId: '',
      errorAlert: false,
      errorMsg: '',
      errorTitle: '',
      gaugeColors: [
        {
          from: 1,
          to: 100,
          color: '#398b87'
        },
      ],
      isLoading: true,
      isLoadingUpdate: false,
      junkFolderId: null,
      notificationMsg: '',
      progressCount: 0,
      progressNotification: false,
      progressNotificationMsg: '',
      selectedCondition: null,
      selectedConditions: [],
      selectedConditionsTab: 0,
      selectedField: "selected",
      selectedTabTypes: ['App'],
      selectedTrackerBtn: 'All',
      showAddConditionDialog: false,
      showAddConditionsDialog: false,
      showAllConds: false,
      showInternalConds: true,
      showBlueprintDialog: false,
      showBulkEditTrackingDialog: false,
      showDispositionWizard: false,
      showEditConditionDialog: false,
      showEncLockWarningDialog: false,
      showErrorDialog: false,
      showLiabilitiesDialog: false,
      sortable: { allowUnsort: false, mode: 'single' },
      successNotification: false,
      underwriterAccessRoles: ['Admin', 'Corp', 'Underwriter'],
      userRolesStorage: localStorage.getItem('user_roles')

    }
  },
  async created() {
  },
  unmounted() {
    document.removeEventListener('click', this.onDocumentClick);
    document.removeEventListener('keydown', this.onDocumentKeydown);
  },
  async mounted() {
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onDocumentKeydown);
    // AJ requested this loan lock warning be disabled
    // await this.$store.dispatch('getLoanLock');
    // let loanLocked = this.$store.state.isLoanLocked;
    //
    // if (loanLocked && !this.$store.state.loanLockDialogOpened) {
    //     this.toggleEncLockWarningDialog();
    //     this.$store.dispatch('updateLoanLockDialogOpened', true);
    // }

    this.setConditionsState();

    await this.getDocuments();

    let purchaseTab = document.querySelector('.k-last.k-item');

    if (purchaseTab) {
      purchaseTab.innerHTML = "<span class='k-link'>Purchase</span><span class='condition--info'><span class='k-icon k-i-information k-i-info'></span> Select a condition to edit</span>";
    }

    this.isLoading = false;
    this.isInitializing = false;

    // Apply filters after initialization is complete
    this.processConditionsFilterSort();
    await this.$nextTick();
    await this.openConditionFromQueryWithRetry();

  },
  beforeUnmount() {
    let condState = {
      sort: this.condSort,
      filter: this.condFilter,
      search: this.condSearchWord,
      showAll: this.showAllConds,
      showInternal: this.showInternalConds,
      selectedConditionsTab: this.selectedConditionsTab,
      selectedTrackerBtn: this.selectedTrackerBtn
    };

    sessionStorage.setItem('condstate', JSON.stringify(condState));
  },
  computed: {
    areAllSelected() {
      return this.conditions.length > 0 && this.conditions.every(cond => cond.selected);
    },
    condColumns() {
      let defaultColumns = [
        { field: 'selected', columnMenu: false, filterable: false, headerSelectionValue: this.areAllSelected },
        { cell: 'hasAttachments', width: '40px', columnMenu: false, filterable: false },
        { field: 'hasAttachments', hidden: true, columnMenu: false, filterable: false, type: "boolean" },
        { field: 'lastTracking', title: 'Tracking' },
        { field: 'category', title: 'Category', hidden: true },
        { field: 'formattedTitle', title: 'Title' },
        { field: 'externalDescription', title: 'Condition Description' },
        { field: 'lastModifiedDate', title: 'Last Status', filter: "date", type: "date", format: "{0:MM/dd/yyyy}" },
      ];

      if (this.hasUnderwriterAccess) {
        defaultColumns.push({ cell: 'duplicateCondition', columnMenu: false, filterable: false });
      }

      return defaultColumns;
    },
    hasUnderwriterAccess(val) {
      return this.userRoles.some(role => this.underwriterAccessRoles.includes(role));
    },
    userRoles() {
      if (this.userRolesStorage) {
        return this.userRolesStorage.split(',');
      }

      return [];
    }
  },
  watch: {
    allConditions(val) {
      if (val) {
        this.getDocuments();
      }
    },
    showAllConds(val) {
      if (!this.isInitializing) {
        this.processConditionsFilterSort();
      }
    },
    showInternalConds(val) {
      if (!this.isInitializing) {
        this.processConditionsFilterSort();
      }
    },
    '$store.state.documents'(val) {
      if (!this.addedConditionTitle) {
        this.getDocuments();
      }
    },
    '$store.state.errorAlert'(val) {
      if (val) {
        this.onRequestError(this.$store.state.errorTitle, this.$store.state.errorMsg);

        let errorData = {
          isError: false,
          title: '',
          msg: ''
        }

        this.$store.commit('updateErrorAlert', errorData);
      }
    },
    '$store.state.notification'(val) {
      if (val) {
        this.showNotification(val);

        this.$store.commit('updateNotification', '');
      }
    }
  },
  methods: {
    // Close the menu before the action runs, so the click doesn't land on a dialog
    // opening underneath it.
    runCondAction(fn) {
      this.condActionsOpen = false;
      if (typeof fn === 'function') fn();
    },
    onDocumentClick() {
      this.condActionsOpen = false;
    },
    onDocumentKeydown(event) {
      if (event.key === 'Escape') this.condActionsOpen = false;
    },
    delay(ms) {
      return new Promise(resolve => setTimeout(resolve, ms));
    },
    async waitFor(predicate, { timeoutMs = 3000, intervalMs = 50 } = {}) {
      const start = Date.now();
      while (true) {
        if (predicate()) return true;
        if (Date.now() - start >= timeoutMs) return false;
        await this.delay(intervalMs);
      }
    },
    getQueryCondId() {
      const id = new URLSearchParams(window.location.search).get('condid');
      return id ? String(id) : '';
    },
    isInternalOnlyCondition(cond) {
      return Array.isArray(cond?.printDefinitions)
        && cond.printDefinitions.length === 1
        && cond.printDefinitions[0] === 'InternalPrint';
    },
    getTabIndexForCondition(cond) {
      const idx = this.conditionsTabs.findIndex(t => t.priorTo?.includes(cond.priorTo));
      return idx >= 0 ? idx : 0;
    },
    async openConditionFromQueryWithRetry() {
      const condId = this.getQueryCondId();
      if (!condId) return;

      const ok = await this.waitFor(() => Array.isArray(this.allConditions) && this.allConditions.length > 0, { timeoutMs: 5000, intervalMs: 50 });
      if (!ok) return;

      const found = this.allConditions.find(c => String(c.id) === condId);
      if (!found) return;

      const tabIndex = this.getTabIndexForCondition(found);

      if (this.selectedConditionsTab !== tabIndex) {
        this.selectedConditionsTab = tabIndex;
        this.assignTabbedConditions();
        await this.$nextTick();
      }

      if (found.cleared && !this.showAllConds) this.showAllConds = true;
      if (this.isInternalOnlyCondition(found) && !this.showInternalConds) this.showInternalConds = true;

      this.processConditionsFilterSort();
      await this.$nextTick();

      await this.waitFor(() => {
        const inTab = this.allTabbedConditions?.some(c => String(c.id) === condId);
        const inGrid = this.conditions?.some(c => String(c.id) === condId);
        return !!(inTab && inGrid);
      }, { timeoutMs: 5000, intervalMs: 50 });

      await this.$nextTick();
      await this.delay(150);

      const toOpen = this.allTabbedConditions?.find(c => String(c.id) === condId) || found;

      this.selectedCondition = this.getConditionAttachments(toOpen);
      if (!this.showEditConditionDialog) this.toggleEditConditionDialog();
    },
    assignTabbedConditions() {
      this.allTabbedConditions = this.allConditions.filter(cond => this.conditionsTabs[this.selectedConditionsTab].priorTo.some(tab => tab === cond.priorTo));

      this.conditions = this.allTabbedConditions.map(cond => ({ ...cond }));

      this.processConditionsFilterSort();
      this.getConditionCategories(this.allConditions);
    },
    async bulkUpdateTracking(tracking) {
      if (this.selectedConditions.length === 0) return;

      this.toggleBulkEditTrackingDialog();

      this.isLoadingUpdate = true;

      let request = {
        conditionIds: this.selectedConditions.map(cond => cond.id),
        conditionTitles: this.selectedConditions.map(cond => cond.title),
        conditionTracking: tracking
      }

      let tokenResponse = await auth.acquireTokenResponse();

      apiService.BulkUpdateConditionTracking(this.loanid, request, tokenResponse.accessToken)
        .then(res => {
          this.selectedConditions = [];
          this.bulkTrackingBtnDisabled = true;

          this.$emit('conditionsupdated', true);

          this.showNotification('Condition(s) tracking updated successfully');
        }).catch(error => {
          this.errorTitle = 'Failed to Update Condition(s) Tracking';

          if (error.response.data.data) {
            this.errorMsg = "Error: \n\n" + error.response.data.data;
          } else {
            this.errorMsg = '';
          }

          this.onRequestError(this.errorTitle, this.errorMsg);
        });
    },
    checkForSelected() {
      if (this.conditions.some(item => item.selected)) return false;

      return true;
    },
    async completeClearToClose(notification) {
      this.toggleDispositionWizard();
      this.showNotification(notification);

      this.$store.dispatch('updateLoan');

      this.$emit('conditionsupdated', true);

      this.showNotification('Generating decision letter...', true);
      await this.$store.dispatch('getClearToCloseLetter');
      this.onCloseNotification('progressNotification');
    },
    async completeConditionalApproval(notification) {
      this.toggleDispositionWizard();
      this.showNotification(notification);

      this.$store.dispatch('updateLoan');

      this.$emit('conditionsupdated', true);

      this.showNotification('Generating decision letter...', true);
      await this.$store.dispatch('getCondlApprovalLetter');
      this.onCloseNotification('progressNotification');
    },
    async completeDeny(notification) {
      this.toggleDispositionWizard();
      this.showNotification(notification);

      this.$store.dispatch('updateLoan');
    },
    async completeRestructure(notification) {
      this.toggleDispositionWizard();
      this.showNotification(notification);

      this.$store.dispatch('updateLoan');
    },
    async completeSuspend(notification) {
      this.toggleDispositionWizard();
      this.showNotification(notification);

      this.$store.dispatch('updateLoan');

      this.showNotification('Generating decision letter...', true);
      await this.$store.dispatch('getSuspensLetter');
      this.onCloseNotification('progressNotification');
    },
    condFilterChange(evt) {
      this.condFilter = evt.filter;

      this.processConditionsFilterSort();
    },
    conditionSortChangeHandler(evt) {
      this.condSort = evt.sort;

      this.conditions = orderBy(this.conditions, this.condSort);
    },
    async generateApprovalLetter() {
      let tokenResponse = await auth.acquireTokenResponse();
      this.idTokenClaims = tokenResponse.idTokenClaims;

      this.showNotification('Generating decision letter...', true);

      apiService.GetLoan(this.loanid, tokenResponse.accessToken)
        .then(res => {
          this.customFields = res.data.data.customFields;

          var CXUWSUSPEND = this.customFields.find(el => el.fieldName === "CX.UW.SUSPEND")?.value ?? false;
          var CXUWICTC = this.customFields.find(el => el.fieldName === "CX.UWI.CTC")?.value ?? false;

          //if not ctc or suspended get the condl approval letter
          if (!CXUWICTC && !CXUWSUSPEND) {
            this.getCondlApprovalLetter();
          }

          //if ctc get the clear to close letter
          if (CXUWICTC) {
            this.getClearToCloseLetter();
          }

          //if suspended get the suspended letter
          if (CXUWSUSPEND) {
            this.getSuspensLetter();
          }

        }).catch(error => {
          console.log(error)
          this.errorTitle = 'Failed to Fetch Loan';

          if (error?.response?.data?.data) {
            this.errorMsg = "Error: \n\n" + error.response?.data?.data;
          } else {
            this.errorMsg = '';
          }
          this.isLoading = false;
          this.onRequestError(this.errorTitle, this.errorMsg);
        });
    },
    async getAttachments() {
      let attachments = this.$store.state.attachments;
      if (attachments.length > 0 && !this.conditionUpdated && !this.addedConditionTitle && !this.documentsUpdated) return this.attachments = attachments.map(att => ({ ...att }));

      await this.$store.dispatch('getAttachments');
      this.attachments = this.$store.state.attachments.map(att => ({ ...att }));
    },
    getAttachmentTitle(id) {
      return this.attachments.find(a => a.id === id).title;
    },
    async getClearToCloseLetter() {
      await this.$store.dispatch('getClearToCloseLetter');
      this.onCloseNotification('progressNotification');
    },
    getConditionAttachments(selectedCondition) {
      if (selectedCondition.assignedTo && selectedCondition.assignedTo.length > 0) {
        let allAttachments = [];

        selectedCondition.assignedTo.forEach(doc => {
          let attachments = this.attachments.filter(att => att.assignedTo?.entityId === doc.entityId);
          allAttachments.push(attachments);
        });

        selectedCondition.attachments = allAttachments.flat();

        selectedCondition.attachments.forEach(att => {
          att.createdDate = formatHelpers.formatDateTime(att.createdDate);
        });
      } else {
        selectedCondition.attachments = [];
      }

      return selectedCondition;
    },
    getConditionCategories(conditions) {
      this.allConditionCategoriesLoaded = false;

      const allCategories = [...new Set(conditions.map((cond) => cond.category))]
        .filter(cond => cond)
        .sort((a, b) => a.localeCompare(b));

      if (allCategories.some(cat => cat === 'Miscellaneous')) {
        allCategories.splice(allCategories.findIndex(cat => cat === 'Miscellaneous'), 1);
        allCategories.push('Miscellaneous');
      }

      this.allConditionCategories = allCategories.map(cat => {
        let categoryConditions = this.allConditions.filter(cond => cond.category === cat);
        let clearedConditions = categoryConditions.filter(cond => cond.tracking.some(track => formatHelpers.clearedTrackingNames.some(name => name === track.status)));
        let categoryCleared = categoryConditions.every(cond => cond.tracking.some(track => formatHelpers.clearedTrackingNames.some(name => name === track.status)));

        return {
          name: cat === 'Miscellaneous' ? 'Misc' : cat,
          cleared: categoryCleared,
          count: categoryConditions.length,
          percentCleared: Math.round((clearedConditions.length / categoryConditions.length) * 100),
          numberCleared: clearedConditions.length,
        }
      });

      let allClearedConditions = this.allConditions.filter(cond => cond.tracking.some(track => formatHelpers.clearedTrackingNames.some(name => name === track.status)));
      let allCategoryCleared = this.allConditions.every(cond => cond.tracking.some(track => formatHelpers.clearedTrackingNames.some(name => name === track.status)));

      let allCategory = {
        name: 'All',
        cleared: allCategoryCleared,
        count: this.allConditions.length,
        percentCleared: Math.round((allClearedConditions.length / this.allConditions.length) * 100),
        numberCleared: allClearedConditions.length,
      }

      this.allConditionCategories.unshift(allCategory);

      this.allConditionCategoriesLoaded = true;
    },
    async getCondlApprovalLetter() {
      await this.$store.dispatch('getCondlApprovalLetter');
      this.onCloseNotification('progressNotification');
    },
    async getDocuments() {
      await this.getAttachments();

      let documents = this.$store.state.documents;
      if (documents.length > 0 && !this.conditionUpdated && !this.addedConditionTitle && !this.documentsUpdated) {
        this.allDocuments = documents.map(doc => ({ ...doc }));

        this.setConditionHasAttachments();

        this.assignTabbedConditions();
      } else {
        await this.$store.dispatch('getDocuments');
        this.allDocuments = this.$store.state.documents.map(doc => ({ ...doc }));

        this.setConditionHasAttachments();

        this.assignTabbedConditions();

        this.documentsUpdated = false;

        if (this.addedConditionTitle) {
          let selectedCondition;

          //to allow duplicate conditions, have to find newest created one to update tracking and auto-open...
          let selectedConditions = this.allConditions.filter(cond => cond.title === this.addedConditionTitle);

          if (selectedConditions.length === 1) {
            selectedCondition = selectedConditions[0];
          }

          if (selectedConditions.length > 1) {
            selectedConditions.sort((a, b) => {
              return b.createdDate - a.createdDate;
            });

            selectedCondition = selectedConditions[0];
          }

          if (selectedCondition) {
            if (this.addedConditionTracking) {
              this.addedConditionId = selectedCondition.id;
              return await this.updateTracking(this.addedConditionId, this.addedConditionTracking);
            } else {
              this.showNotification('Condition added successfully');

              if (this.duplicatedCondition) {
                this.conditionUpdated = false;
                this.addedConditionTitle = '';
                return this.toggleAddConditionDialog();
              } else {
                this.toggleAddConditionDialog();
                this.onEditCondition({ dataItem: selectedCondition });
              }
            }
          }
        }

        if (this.conditionUpdated && this.selectedCondition?.id === this.editedConditionId && this.showEditConditionDialog) {
          this.selectedCondition = this.getConditionAttachments(this.allTabbedConditions.find(cond => cond.id === this.selectedCondition.id));
          this.conditionUpdated = false;
        }
      }
      this.isLoadingUpdate = false;
    },
    async getSuspensLetter() {
      await this.$store.dispatch('getSuspensLetter');
      this.onCloseNotification('progressNotification');
    },
    onAddCondition() {
      this.toggleAddConditionDialog();
    },
    onAddConditions() {
      this.toggleAddConditionsDialog();
    },
    onBulkEditTracking() {
      this.selectedConditions = this.allTabbedConditions.filter(cond => cond.selected);

      this.toggleBulkEditTrackingDialog();
    },
    onCategoryFilter(category) {
      if (this.selectedTrackerBtn === 'All' && category === 'All') return;

      //clicking an already selected tracker btn (besides 'All') turns it off, setting the filter back to 'All'
      if (this.selectedTrackerBtn === category) category = 'All';

      if (this.condFilter && this.condFilter.filters.length > 0) {
        let currentCatFilterIndex = this.condFilter.filters.findIndex(filter => filter.field === 'category');

        if (currentCatFilterIndex >= 0) {
          if (category !== 'All') {
            this.condFilter.filters.splice(currentCatFilterIndex, 1, { field: 'category', operator: 'contains', value: category });
          } else {
            this.condFilter.filters.splice(currentCatFilterIndex, 1);
          }
        } else {
          this.condFilter.filters.push({ field: 'category', operator: 'contains', value: category });
        }
      } else {
        if (category !== 'All') {
          this.condFilter = { logic: 'and', filters: [{ field: 'category', operator: 'contains', value: category }] };
        }
      }

      this.selectedTrackerBtn = category;
      this.processConditionsFilterSort();
    },
    onCloseNotification(flag) {
      if (flag === 'progressNotification') {
        this.progressCount -= 1;
        if (this.progressCount === 0) {
          this[flag] = false;
        }
      } else {
        this[flag] = false;
      }
    },
    onConditionAdded(addedConditionTitle, addedConditionTracking = null) {
      this.addedConditionTitle = addedConditionTitle;
      this.addedConditionTracking = cloneDeep(addedConditionTracking);

      this.$emit('conditionsupdated', true);
    },
    onConditionsAdded() {
      this.$emit('conditionsupdated', true);
      this.onCloseNotification('progressNotification');
      this.showNotification('Condition(s) added successfully')
    },
    onConditionsTabSelected(evt) {
      this.selectedConditionsTab = evt.selected;
      this.assignTabbedConditions();
    },
    onConditionTrackingUpdated(condition, tracking) {
      this.toggleEditConditionDialog();
      this.showNotification('Updating Condition...', true);

      this.updateTracking(condition, tracking);
    },
    onConditionUpdated(conditionId) {
      this.editedConditionId = conditionId;
      this.conditionUpdated = true;

      this.$emit('conditionsupdated', true);
    },
    onDuplicateCondition(condition) {
      this.duplicatedCondition = condition;
      this.toggleAddConditionDialog();
    },
    onEditCondition(evt) {
      if (evt.event) {
        if (Array.from(evt.event.target?.children).some(child => Array.from(child.classList).includes('k-checkbox-label'))) return;
      }

      this.selectedCondition = this.getConditionAttachments(evt.dataItem);

      this.toggleEditConditionDialog();
    },
    onFilterConditions(evt) {
      this.condSearchWord = evt.value;

      this.processConditionsFilterSort();
    },
    onHeaderSelectionChange(event) {
      if (!event.event) return;

      let checked = event.event.target.checked;

      this.conditions.forEach((item) => {
        item.selected = checked;

        let tabbedCondition = this.allTabbedConditions.find(cond => cond.id === item.id);

        if (tabbedCondition) {
          tabbedCondition.selected = checked;
        }
      });

      const grid = document.querySelector('.open-conditions-card .k-grid-content');
      let inputs = grid.querySelectorAll("input[type='checkbox']");

      if (checked) {
        inputs.forEach(input => {
          input.checked = checked;
          input.closest('tr').classList.add('k-selected');
        });
      } else {
        inputs.forEach(input => {
          input.checked = checked;
          input.closest('tr').classList.remove('k-selected');
        });
      }

      this.bulkTrackingBtnDisabled = !checked;
    },
    onRequestError(errorTitle, errorMsg) {
      this.isLoading = false;
      this.isLoadingUpdate = false;
      this.errorAlert = true;

      this.errorTitle = errorTitle;

      if (errorMsg) {
        this.errorMsg = errorMsg;
      } else {
        this.errorMsg = 'Please try again';
      }

      this.showErrorDialog = true;
    },
    async onRunSwift() {
      this.isLoading = true;

      await this.$store.dispatch('runSwift');
      this.$emit('conditionsupdated', true);

      this.isLoading = false;
      this.showNotification('SwiftAI ran successfully');
    },
    onSelectionChange(event) {
      if (!event.event) return;

      event.dataItem[this.selectedField] = event.event.target.checked;
      let itemIndex;

      itemIndex = this.conditions.findIndex(i => i.id === event.dataItem.id);

      if (itemIndex >= 0) {
        this.conditions[itemIndex][this.selectedField] = event.event.target.checked;

        let tabbedCondition = this.allTabbedConditions.find(cond => cond.id === this.conditions[itemIndex].id);

        if (tabbedCondition) {
          tabbedCondition.selected = event.event.target.checked;
        }
      }

      if (event.dataItem[this.selectedField] === true) {
        event.event.target.closest('tr').classList.add('k-selected');
        this.bulkTrackingBtnDisabled = false;
      } else {
        event.event.target.closest('tr').classList.remove('k-selected');
        this.bulkTrackingBtnDisabled = this.checkForSelected();
      }
    },
    processConditionsFilterSort() {
      if (this.condSearchWord) {
        this.conditions = this.allTabbedConditions.filter(item => {
          return Object.values(item).some(val => val && val.toString().toLowerCase().includes(this.condSearchWord.toLowerCase()));
        });
      } else {
        this.conditions = this.allTabbedConditions.map(cond => ({ ...cond }));
      }

      if (this.condFilter) {
        this.conditions = filterBy(this.conditions, this.condFilter);
      }

      if (!this.showAllConds) {
        this.conditions = this.conditions.filter(cond => !cond.cleared);
      }

      // Filter based on Show Internal toggle
      if (!this.showInternalConds) {
        // When toggle is OFF, hide internal-only conditions
        this.conditions = this.conditions.filter(cond => {
          // If printDefinitions doesn't exist or is empty, show it (treat as external)
          if (!cond.printDefinitions || cond.printDefinitions.length === 0) {
            return true;
          }
          // Hide conditions that ONLY have InternalPrint
          if (cond.printDefinitions.length === 1 && cond.printDefinitions[0] === 'InternalPrint') {
            return false;
          }
          // Show everything else (has ExternalPrint or both)
          return true;
        });
      }

      if (this.condSort.length > 0) {
        this.conditionSortChangeHandler({ sort: this.condSort });
      }
    },
    setConditionHasAttachments() {
      //make sure at least one document bucket assigned to a condition has an attachment for it to have an icon in the 'has document' column...
      this.allConditions.forEach(cond => {
        cond.hasAttachments = false;

        if (cond.assignedTo && cond.assignedTo.length > 0) {
          cond.assignedTo.forEach(doc => {
            let document = this.allDocuments.find(d => d.id === doc.entityId);

            if (document && document.attachmentCount && document.attachmentCount > 0) {
              cond.hasAttachments = true;
            }
          });
        }
      });
    },
    setConditionsState() {
      let condState = sessionStorage.getItem('condstate');

      if (condState) {
        condState = JSON.parse(condState);
        console.log('Loading saved condState:', condState);
        console.log('showInternal in saved state:', condState.showInternal);

        this.condSearchWord = condState.search;
        this.condSort = condState.sort;
        this.showAllConds = condState.showAll;
        // Always default to true - ignore saved state for now since it was incorrectly saved as false
        this.showInternalConds = true;
        // Later you can change this back to: this.showInternalConds = 'showInternal' in condState ? condState.showInternal : true;
        console.log('showInternalConds after loading:', this.showInternalConds);
        this.selectedConditionsTab = +condState.selectedConditionsTab;
        this.selectedTrackerBtn = condState.selectedTrackerBtn;

        if (condState.filter && condState.filter.filters.length > 0) {
          condState.filter.filters.forEach(filter => {
            if (this.dateColumns.some(col => col === filter.field)) {
              filter.value = formatHelpers.formatDate(filter.value);
            }
          })
        }

        this.condFilter = condState.filter;
      } else {
        console.log('No saved condState, using defaults');
        // Set default values when no saved state exists
        this.showInternalConds = true;
        console.log('showInternalConds set to default:', this.showInternalConds);

        let currentMilestone = sessionStorage.getItem('currentmilestone');

        if (currentMilestone === 'Submittal') {
          this.condSort.push({ field: 'formattedTitle', dir: 'asc' });
        } else {
          this.condSort.push({ field: 'lastModifiedDate', dir: 'desc' });
        }
      }
    },
    showNotification(notificationMsg, progress = false) {
      if (progress) {
        this.progressNotificationMsg = notificationMsg;

        this.progressCount += 1;
        this.progressNotification = true;
      } else {
        this.notificationMsg = notificationMsg;

        this.successNotification = true;

        setTimeout(() => {
          this.successNotification = false;
        }, 4000);
      }
    },
    toggleAddConditionDialog() {
      this.showAddConditionDialog = !this.showAddConditionDialog;

      if (!this.showAddConditionDialog) {
        this.duplicatedCondition = null;
      }
    },
    toggleAddConditionsDialog() {
      this.showAddConditionsDialog = !this.showAddConditionsDialog;
    },
    toggleBlueprintDialog() {
      this.showBlueprintDialog = !this.showBlueprintDialog;
    },
    toggleBulkEditTrackingDialog() {
      this.showBulkEditTrackingDialog = !this.showBulkEditTrackingDialog;
    },
    toggleDispositionWizard() {
      this.showDispositionWizard = !this.showDispositionWizard;
    },
    toggleEditConditionDialog() {
      this.addedConditionTitle = '';
      this.addedConditionId = '';

      this.showEditConditionDialog = !this.showEditConditionDialog;
    },
    toggleEncLockWarningDialog() {
      this.showEncLockWarningDialog = !this.showEncLockWarningDialog;
    },
    toggleErrorDialog() {
      this.showErrorDialog = !this.showErrorDialog;

      if (!this.showErrorDialog) {
        this.errorAlert = false;
        this.errorMsg = '';
      }
    },
    toggleLiabilitiesDialog() {
      this.showLiabilitiesDialog = !this.showLiabilitiesDialog;
    },
    async updateCondition(editedCondition, trackingUpdated, tracking = null) {
      this.toggleEditConditionDialog();
      this.showNotification('Updating Condition...', true);

      let tokenResponse = await auth.acquireTokenResponse();
      this.idTokenClaims = tokenResponse.idTokenClaims;

      apiService.UpdateLoanCondition(this.loanid, editedCondition, tokenResponse.accessToken)
        .then(res => {
          if (!trackingUpdated) {
            this.onConditionUpdated(editedCondition.id);

            this.onCloseNotification('progressNotification');
            this.showNotification('Condition updated successfully');
          } else {
            this.updateTracking(editedCondition, tracking);
          }
        }).catch(error => {
          this.onCloseNotification('progressNotification');

          this.errorTitle = 'Failed to Update Condition';

          if (error?.response?.data?.data) {
            this.errorMsg = "Error: \n\n" + error.response.data.data;
          } else {
            this.errorMsg = '';
          }

          this.onRequestError(this.errorTitle, this.errorMsg);
        });
    },
    async updateDocuments() {
      await this.$store.dispatch('getAttachments');
      this.$store.dispatch('getDocuments');
    },
    async updateTracking(condition, tracking) {
      let request = {
        loanId: this.loanid,
        conditionId: condition.id,
        conditionName: condition.title,
        conditionTracking: tracking
      };

      let tokenResponse = await auth.acquireTokenResponse();
      this.idTokenClaims = tokenResponse.idTokenClaims;

      return apiService.UpdateConditionTracking(request, tokenResponse.accessToken)
        .then(res => {
          if (!this.addedConditionTracking) {
            this.onConditionUpdated(condition.id);

            this.onCloseNotification('progressNotification');
            this.showNotification('Condition updated successfully');
          } else {
            this.addedConditionTracking = null;

            this.$emit('conditionsupdated', true);
          }
        }).catch(error => {
          this.showAddConditionDialog = false;
          this.onCloseNotification('progressNotification');

          this.errorTitle = 'Failed to Update Condition Tracking';

          if (error?.response?.data?.data) {
            this.errorMsg = "Error: \n\n" + error.response?.data?.data;
          } else {
            this.errorMsg = '';
          }

          this.onRequestError(this.errorTitle, this.errorMsg);
        });
    }
  }
}
</script>

<style>
@import '/src/assets/css/conditions.css';
</style>
